import { get } from 'lodash'
import { latest, all, full, pre, matching, DESKTOP_SUBSETTER_LAST_VERSION } from 'app/releases'
import { determinations } from 'app/utils/subscription'
import { paramsToQuery } from 'app/views/search/utils'
import accounting from 'accounting'
import v6Icons from 'app/icons/v6'
import v7Icons from 'app/icons/v7'

export const majorVersionsList = [
  { prefix: 'five', label: 'v5' },
  { prefix: 'six', label: 'v6' },
  { prefix: 'seven', label: 'v7' }
]

function normalize(vm, params = {}) {
  const { resolved = false } = params

  const guessedVersion = get(vm, ['$route', 'meta', 'version'], resolved ? latest.together.version : 'latest')

  const { hash = null } = params
  const hashParam = hash ? { hash } : {}

  let { version = guessedVersion, versions } = params

  if (version === 'latest' && resolved) {
    version = latest.together.version
  }

  if (versions === 'latest') {
    versions = all.v7
      .filter((version) => {
        return version.versionParts.major === latest.together.versionParts.major && version.versionParts.minor === latest.together.versionParts.minor
      })
      .map((version) => {
        return version.version
      })
  }

  return {
    ...params,
    version,
    versions,
    hash: hashParam
  }
}

export default {
  /**
   * Configures some of the sites global plugins, such as $search and $icons.
   *
   * Convenience Links:
   * {@link https://vuejs.org/guide/reusability/plugins.html Vue Plugin: install}
   * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/defineProperty JS Object: defineProperty}
   * {@link https://v2.vuejs.org/v2/cookbook/adding-instance-properties.html#The-Context-of-Prototype-Methods Vue Properties: prototype}
   *
   * @param {Object} Vue - The Vue object
   * @param {Object} options - An optional object for setting options, currently unused in this context
   */
  install(Vue, options = {}) {
    Object.defineProperty(Vue.prototype, '$releases', {
      get() {
        const active = matching({
          version: get(this, ['$route', 'meta', 'version'], latest.together.version)
        })

        return {
          active,
          latest,
          all,
          full,
          pre
        }
      }
    })

    Object.defineProperty(Vue.prototype, '$v6', {
      get() {
        return v6Icons
      }
    })

    Object.defineProperty(Vue.prototype, '$v7', {
      get() {
        return v7Icons
      }
    })

    Object.defineProperty(Vue.prototype, '$layoutStyleSheetName', {
      get() {
        return document.querySelector('link:not([disabled])[data-purpose="Layout StyleSheet"]').getAttribute('title')
      }
    })

    Object.defineProperty(Vue.prototype, '$permits', {
      get() {
        const vm = this

        return {
          all: vm.$store.getters['permits/all'],
          has: vm.$store.getters['permits/has'],
          find: vm.$store.getters['permits/find'],
          flagged(type, flag) {
            const { permit } = vm.$store.getters['permits/find'](type)
            const flags = get(permit, ['flags'], [])

            return flags.indexOf(flag) > -1
          },
          get(type, prop, def = null) {
            const { nope, permit } = vm.$store.getters['permits/find'](type)

            if (nope) return def

            return get(permit, prop, def)
          }
        }
      }
    })

    Object.defineProperty(Vue.prototype, '$dt', {
      get() {
        const vm = this
        const subscription = vm.$store.getters['subscriptions/currentSubscription']
        const isBacker = get(vm, ['$store', 'getters', 'accounts/currentAccount', 'isBacker'])
        const permits = vm.$store.getters['permits/all']

        return determinations({ subscription, isBacker, permits })
      }
    })

    function roundDownToNearest(count, place = 1) {
      const roundedIcons = Math.floor(count / place) * place

      return accounting.formatNumber(roundedIcons)
    }

    function formatted(value) {
      return {
        value,
        formatted: accounting.formatNumber(value),
        roundedDownToNearestHundred: roundDownToNearest(value, 100),
        roundedDownToNearestThousand: roundDownToNearest(value, 1000)
      }
    }

    Object.defineProperty(Vue.prototype, '$counts', {
      get() {
        const pro = get(this.$releases, ['active', 'iconCount', 'pro'], 0) // eslint-disable-line no-undef
        const free = get(this.$releases, ['active', 'iconCount', 'free'], 0) // eslint-disable-line no-undef
        const all = pro + free

        return {
          totalLatestStylesWithBrands: get(this.$releases, ['active', 'familyStyles']).length,
          totalLatestStylesWithoutBrands: get(this.$releases, ['active', 'familyStyles']).filter((o) => o.style !== 'brands').length,
          icons: {
            all: formatted(all),
            pro: formatted(pro),
            free: formatted(free)
          }
        }
      }
    })

    Object.defineProperty(Vue.prototype, '$dlproplus', {
      get() {
        return {
          path: (params = {}) => {
            const { on, version } = normalize(this, { ...params, resolved: true })
            const {
              download: { separatesWebDesktop }
            } = matching({ version })

            if (on && separatesWebDesktop) {
              return `/releases/${version}/pro-plus/${on}/download`
            } else {
              return `/releases/${version}/pro-plus/download`
            }
          }
        }
      }
    })

    Object.defineProperty(Vue.prototype, '$dlpro', {
      get() {
        const instance = this

        return {
          path: (params = {}) => {
            const { on, version } = normalize(this, { ...params, resolved: true })
            const {
              download: { separatesWebDesktop }
            } = matching({ version })

            if (on && separatesWebDesktop) {
              return `/releases/${version}/${on}/download`
            } else {
              return `/releases/${version}/download`
            }
          },

          get allowed() {
            return instance.$store.getters['licenses/evidenceOfProLicense'] && instance.$store.getters['sessions/evidenceOfPastSession']
          }
        }
      }
    })

    Object.defineProperty(Vue.prototype, '$dlnpm', {
      get() {
        return {
          path: (params = {}) => {
            const { name, version } = normalize(this, { ...params, resolved: true })

            return `/releases/${version}/npm/${name}/download`
          }
        }
      }
    })

    Object.defineProperty(Vue.prototype, '$dlsubsetter', {
      get() {
        return {
          path: (params = {}) => {
            const { os, version } = normalize(this, { ...params, version: DESKTOP_SUBSETTER_LAST_VERSION, resolved: true })

            return `/releases/${version}/subsetter/${os}/download`
          }
        }
      }
    })

    Object.defineProperty(Vue.prototype, '$dlfree', {
      get() {
        return {
          path: (params = {}) => {
            const { on, version } = normalize(this, { ...params, resolved: true })
            const {
              download: { separatesWebDesktop }
            } = matching({ version })

            const baseUrl = `${FONTAWESOME.FONTAWESOME_FREE_BASE_URL}/releases/v${version}`

            if (on && separatesWebDesktop) {
              return `${baseUrl}/fontawesome-free-${version}-${on}.zip`
            } else {
              return `${baseUrl}/fontawesome-free-${version}.zip`
            }
          }
        }
      }
    })

    Object.defineProperty(Vue.prototype, '$versionRouteName', {
      get() {
        return (name, params = {}) => {
          let prefix = get(params, ['prefix']) || 'latest'

          return `${prefix}.${name}`
        }
      }
    })

    Object.defineProperty(Vue.prototype, '$docs', {
      get() {
        const createDocsRoute = (pathMatch = '', opts = {}) => {
          const { prefix } = opts
          const loc = {
            name: this.$versionRouteName('docs', { prefix }),
            params: { pathMatch }
          }

          if (opts.hash) {
            loc.hash = opts.hash
          }

          return loc
        }

        const docs = Object.assign(
          (pathMatch = '', opts = {}) => createDocsRoute(pathMatch, opts),
          Object.fromEntries(
            majorVersionsList.map((version) => {
              return [
                version.label,
                (pathMatch = '', opts = {}) => {
                  return createDocsRoute(pathMatch, { ...opts, prefix: version.prefix })
                }
              ]
            })
          )
        )

        return docs
      }
    })

    Object.defineProperty(Vue.prototype, '$icon', {
      get() {
        const fnc = ({ name: icon, style: s = null, hash = null, prefix }) => {
          const loc = {
            name: this.$versionRouteName('icons.detail', { prefix }),
            params: { icon },
            query: { s }
          }

          if (hash) {
            loc.hash = hash
          }

          return loc
        }

        majorVersionsList.forEach((version) => {
          fnc[version.label] = (params = {}) => fnc({ ...params, prefix: version.prefix })
        })

        return fnc
      }
    })

    Object.defineProperty(Vue.prototype, '$pack', {
      get() {
        const fnc = ({ name: pack, prefix }) => {
          return { name: this.$versionRouteName('icons.packs.detail', { prefix }), params: { pack } }
        }

        majorVersionsList.forEach((version) => {
          fnc[version.label] = (params = {}) => fnc({ ...params, prefix: version.prefix })
        })

        fnc.v5 = (params = {}) => {
          throw new Error('Icon Pack Landing views are not available for versions below v6')
        }

        return fnc
      }
    })

    Object.defineProperty(Vue.prototype, '$category', {
      get() {
        const fnc = ({ name: category, prefix }) => {
          return { name: this.$versionRouteName('icons.categories.detail', { prefix }), params: { category } }
        }

        majorVersionsList.forEach((version) => {
          fnc[version.label] = (params = {}) => fnc({ ...params, prefix: version.prefix })
        })

        return fnc
      }
    })

    Object.defineProperty(Vue.prototype, '$search', {
      // WARNING: modifying this without also updating algoliaRouteState.js could create problems!
      get() {
        const fnc = (params = {}) => {
          const { prefix, ...searchParams } = params
          const query = paramsToQuery(searchParams)

          return { name: this.$versionRouteName('search', { prefix }), query }
        }

        majorVersionsList.forEach((version) => {
          fnc[version.label] = (params = {}) => fnc({ ...params, prefix: version.prefix })
        })

        return fnc
      }
    })

    Object.defineProperty(Vue.prototype, '$icons', {
      get() {
        const fnc = () => {
          return { name: this.$versionRouteName('icons') }
        }

        fnc.v6 = () => {
          return { name: this.$versionRouteName('icons', { prefix: 'six' }) }
        }

        fnc.v5 = () => {
          throw new Error('No icon discovery for v5')
        }

        return fnc
      }
    })

    Object.defineProperty(Vue.prototype, '$upgrade', {
      get() {
        return {
          toNextPlan: () => {
            const vm = this
            const subscription = vm.$store.getters['subscriptions/currentSubscription']

            switch (subscription.plan.sku) {
              case 'FA-FREE':
                return { name: 'plans.pro' }
              case 'FA-LITE':
                return { name: 'plans.pro' }
              case 'FA-STANDARD':
                return { name: 'plans.pro-plus' }
              case 'FA-LITE-PLUS':
                return { name: 'plans.pro-plus' }
              case 'FA-STANDARD-PLUS':
                return { name: 'plans.pro-max-plus' }
              case 'WA-FA-BUNDLE-BACKER':
                return { name: 'plans.pro-plus' }
              case 'WA-FA-ONLY-BACKER':
                return { name: 'plans.pro-plus' }
              case 'WA-FA-TEAM-BACKER':
                return { name: 'plans.pro-plus' }
              case 'FA-BACKER':
                return { name: 'plans.pro-plus' }
            }
          }
        }
      }
    })
  }
}
